import { Component, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExamenesService, Examen, Pregunta, Intento, RespuestaIntento } from '../services/examenes.service';
import { NotificationService } from '../services/notification.service';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-exam-take',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './exam-take.html',
  styleUrl: './exam-take.css',
})
export class ExamTakeComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private examenesService = inject(ExamenesService);
  private notificationService = inject(NotificationService);

  // Estado de carga
  isLoading = signal(false);
  
  // Datos del examen
  examen = signal<Examen | null>(null);
  intento = signal<Intento | null>(null);
  
  // Navegación de preguntas
  currentQuestionIndex = signal(0);
  respuestas = new Map<number, RespuestaIntento>();
  
  // Temporizador
  tiempoRestante = signal<number>(0);
  tiempoTranscurrido = signal<number>(0);
  private timerSubscription?: Subscription;
  
  // Estado del examen
  examenIniciado = signal(false);
  examenFinalizado = signal(false);
  mostrandoResultados = signal(false);
  resultados = signal<any>(null);
  
  // Intentos
  intentosRealizados = signal(0);
  intentosDisponibles = signal(true);
  intentoEnProgreso = signal<Intento | null>(null);
  todosLosIntentos = signal<any[]>([]);
  mostrarHistorialIntentos = signal(false);
  
  // UI
  showConfirmDialog = signal(false);
  showTimeWarning = signal(false);
  
  // Query params
  private courseId: number | null = null;
  private examId: number | null = null;

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.examId = +params['id'];
      
      this.route.queryParams.subscribe(queryParams => {
        this.courseId = queryParams['course'] ? +queryParams['course'] : null;
        
        if (this.examId) {
          this.cargarExamen(this.examId);
        }
      });
    });
  }

  ngOnDestroy() {
    this.detenerTemporizador();
  }

  /**
   * Carga los datos del examen y verifica intentos previos
   */
  cargarExamen(idExamen: number) {
    this.isLoading.set(true);
    
    // Primero obtener información de intentos previos
    this.examenesService.getMisIntentos(idExamen).subscribe({
      next: (intentosResponse) => {
        // Cargar datos del examen
        this.examenesService.getExamen(idExamen).subscribe({
          next: (response) => {
            this.isLoading.set(false);
            
            if (response.ok && response.data) {
              // VALIDAR Y LIMPIAR DUPLICADOS INMEDIATAMENTE AL RECIBIR LOS DATOS
              const examenRecibido = response.data;
              console.log(`🔍 [DEBUG] Examen recibido del backend - ID: ${examenRecibido.id_examen}`);
              console.log(`📊 [DEBUG] Total de preguntas recibidas: ${examenRecibido.preguntas?.length || 0}`);
              console.log(`🌍 [DEBUG] Zona horaria del navegador: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
              console.log(`🕐 [DEBUG] Hora local: ${new Date().toLocaleString()}`);
              
              // Detectar y limpiar duplicados ANTES de establecer el examen
              if (examenRecibido.preguntas && examenRecibido.preguntas.length > 0) {
                const preguntasOriginales = examenRecibido.preguntas.length;
                const preguntasLimpias = this.limpiarPreguntasDuplicadas(examenRecibido.preguntas);
                
                if (preguntasLimpias.length !== preguntasOriginales) {
                  console.error(`🚨 [ALERTA] Se detectaron ${preguntasOriginales - preguntasLimpias.length} preguntas duplicadas del backend`);
                  console.error(`🚨 [ALERTA] Examen ID: ${examenRecibido.id_examen}, Usuario timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
                  
                  // Enviar información de debug al backend (opcional)
                  this.reportarDuplicadosAlBackend(examenRecibido.id_examen, preguntasOriginales, preguntasLimpias.length);
                }
                
                examenRecibido.preguntas = preguntasLimpias;
              }
              
              this.examen.set(examenRecibido);
              
              // Verificar intentos disponibles
              if (intentosResponse.ok && intentosResponse.data) {
                const intentos = intentosResponse.data;
                this.intentosRealizados.set(intentos.length);
                this.todosLosIntentos.set(intentos);
                
                // Verificar si hay un intento en progreso
                const intentoActivo = intentos.find((i: any) => i.estado === 'En_Progreso');
                if (intentoActivo) {
                  this.intentoEnProgreso.set(intentoActivo);
                  this.notificationService.warning(
                    'Intento en progreso',
                    'Tienes un intento sin finalizar. Puedes continuar donde lo dejaste.'
                  );
                }
                
                // Verificar si quedan intentos disponibles
                const examen = examenRecibido;
                if (examen.intentos_permitidos > 0 && intentos.length >= examen.intentos_permitidos) {
                  this.intentosDisponibles.set(false);
                  
                  // Si ya no hay intentos disponibles, mostrar historial
                  console.log('📋 Sin intentos disponibles, mostrando historial');
                  this.mostrarHistorialIntentos.set(true);
                  
                  this.notificationService.info(
                    'Intentos agotados',
                    `Has utilizado todos tus intentos disponibles (${examen.intentos_permitidos})`
                  );
                }
              }
            } else {
              this.notificationService.error(
                'Error',
                response.msg || 'No se pudo cargar el examen'
              );
              this.goBack();
            }
          },
          error: (error) => {
            this.isLoading.set(false);
            console.error('Error al cargar examen:', error);
            this.notificationService.error(
              'Error de Conexión',
              'No se pudo conectar con el servidor'
            );
            this.goBack();
          }
        });
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al verificar intentos:', error);
        // Continuar aunque falle la verificación de intentos
        this.cargarSoloExamen(idExamen);
      }
    });
  }

  /**
   * Carga solo los datos del examen (fallback)
   */
  private cargarSoloExamen(idExamen: number) {
    this.examenesService.getExamen(idExamen).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.ok && response.data) {
          const examenRecibido = response.data;
          
          // Validar duplicados también en el fallback
          if (examenRecibido.preguntas && examenRecibido.preguntas.length > 0) {
            const preguntasOriginales = examenRecibido.preguntas.length;
            const preguntasLimpias = this.limpiarPreguntasDuplicadas(examenRecibido.preguntas);
            
            if (preguntasLimpias.length !== preguntasOriginales) {
              console.error(`🚨 [ALERTA FALLBACK] Duplicados detectados: ${preguntasOriginales - preguntasLimpias.length}`);
              this.reportarDuplicadosAlBackend(examenRecibido.id_examen, preguntasOriginales, preguntasLimpias.length);
            }
            
            examenRecibido.preguntas = preguntasLimpias;
          }
          
          this.examen.set(examenRecibido);
        } else {
          this.notificationService.error('Error', response.msg || 'No se pudo cargar el examen');
          this.goBack();
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al cargar examen:', error);
        this.goBack();
      }
    });
  }

  /**
   * Inicia el intento de examen
   */
  iniciarExamen() {
    if (!this.examId) return;
    
    // Verificar si quedan intentos disponibles
    if (!this.intentosDisponibles()) {
      this.notificationService.error(
        'Sin intentos',
        'Ya no tienes intentos disponibles para este examen'
      );
      return;
    }
    
    this.isLoading.set(true);
    
    this.examenesService.iniciarIntento(this.examId).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok && response.data) {
          // El backend puede devolver un intento existente o uno nuevo
          const data = response.data as any;
          
          // Si ya hay un intento en progreso
          if (data.intento) {
            this.intento.set(data.intento);
            
            // Variable para determinar si es un intento nuevo
            const esIntentoNuevo = !response.msg?.includes('en progreso');
            
            // Cargar las preguntas del intento
            if (data.preguntas && data.preguntas.length > 0) {
              const examen = this.examen();
              if (examen) {
                console.log(`🔍 [INTENTO] Preguntas recibidas del intento: ${data.preguntas.length}`);
                
                // SIEMPRE LIMPIAR DUPLICADOS primero, sin importar si es nuevo o no
                const preguntasLimpias = this.limpiarPreguntasDuplicadas(data.preguntas);
                
                if (preguntasLimpias.length !== data.preguntas.length) {
                  console.error(`🚨 [ALERTA INTENTO] Duplicados en intento: ${data.preguntas.length - preguntasLimpias.length}`);
                  this.reportarDuplicadosAlBackend(examen.id_examen, data.preguntas.length, preguntasLimpias.length);
                }
                
                // Actualizar el examen con las preguntas limpias del intento
                this.examen.set({
                  ...examen,
                  preguntas: preguntasLimpias
                });
                
                // APLICAR ALEATORIZACIÓN SOLO EN INTENTOS NUEVOS
                if (esIntentoNuevo) {
                  // Aplicar aleatorización según configuración del examen
                  if (examen.aleatorizar_preguntas) {
                    console.log('🔀 Aleatorizando preguntas...');
                    this.aleatorizarPreguntas();
                  }
                  
                  if (examen.aleatorizar_opciones) {
                    console.log('🔀 Aleatorizando opciones...');
                    this.aleatorizarOpciones();
                  }
                }
              }
            }
            
            this.examenIniciado.set(true);
            
            // Calcular tiempo restante si hay un intento en progreso
            const intento = data.intento;
            const examen = this.examen();
            
            if (examen && examen.tiempo_limite && examen.tiempo_limite > 0) {
              const fechaInicio = new Date(intento.fecha_inicio).getTime();
              const ahora = new Date().getTime();
              const tiempoTranscurridoSeg = Math.floor((ahora - fechaInicio) / 1000);
              const tiempoLimiteSeg = examen.tiempo_limite * 60;
              const tiempoRestanteSeg = Math.max(0, tiempoLimiteSeg - tiempoTranscurridoSeg);
              
              this.tiempoRestante.set(tiempoRestanteSeg);
              this.tiempoTranscurrido.set(tiempoTranscurridoSeg);
              
              if (tiempoRestanteSeg > 0) {
                this.iniciarTemporizador();
              } else {
                this.notificationService.error('Tiempo agotado', 'El tiempo del examen ha finalizado');
                this.finalizarExamen();
                return;
              }
            }
            
            if (response.msg?.includes('en progreso')) {
              this.notificationService.info(
                'Continuar examen',
                'Continuando con tu intento anterior'
              );
            } else {
              this.notificationService.success(
                '¡Examen iniciado!',
                'Tienes ' + (examen?.tiempo_limite ? examen.tiempo_limite + ' minutos' : 'tiempo ilimitado')
              );
            }
          } else {
            // Respuesta en formato antiguo (compatibilidad)
            this.intento.set(response.data);
            this.examenIniciado.set(true);
            
            const examen = this.examen();
            if (examen && examen.tiempo_limite && examen.tiempo_limite > 0) {
              this.tiempoRestante.set(examen.tiempo_limite * 60);
              this.iniciarTemporizador();
            }
            
            // Verificar y limpiar duplicados en formato antiguo
            const examenActual = this.examen();
            if (examenActual && examenActual.preguntas) {
              const preguntasOriginales = examenActual.preguntas.length;
              const preguntasLimpias = this.limpiarPreguntasDuplicadas(examenActual.preguntas);
              
              if (preguntasLimpias.length !== preguntasOriginales) {
                console.error(`🚨 [ALERTA ANTIGUO] Duplicados: ${preguntasOriginales - preguntasLimpias.length}`);
              }
              
              this.examen.set({ ...examenActual, preguntas: preguntasLimpias });
            }
            
            // Aplicar aleatorización en formato antiguo
            if (examen?.aleatorizar_preguntas) {
              this.aleatorizarPreguntas();
            }
            if (examen?.aleatorizar_opciones) {
              this.aleatorizarOpciones();
            }
            
            this.notificationService.success('¡Examen iniciado!', 'Buena suerte');
          }
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo iniciar el examen'
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al iniciar examen:', error);
        this.notificationService.error(
          'Error',
          error.error?.msg || 'No se pudo iniciar el examen'
        );
      }
    });
  }

  /**
   * Guarda la respuesta de una pregunta
   */
  guardarRespuesta(pregunta: Pregunta, idOpcion?: number, respuestaTexto?: string) {
    if (!this.intento()) return;
    
    const respuesta: RespuestaIntento = {
      id_pregunta: pregunta.id_pregunta,
      id_opcion: idOpcion,
      respuesta_texto: respuestaTexto,
      tiempo_respuesta: this.tiempoTranscurrido()
    };
    
    // Guardar en el mapa local
    this.respuestas.set(pregunta.id_pregunta, respuesta);
    
    // Enviar al backend
    this.examenesService.responderPregunta(this.intento()!.id_intento, respuesta).subscribe({
      next: (response) => {
        if (response.ok) {
          console.log('✅ Respuesta guardada:', pregunta.id_pregunta);
        }
      },
      error: (error) => {
        console.error('Error al guardar respuesta:', error);
      }
    });
  }

  /**
   * Navega a la siguiente pregunta
   */
  siguientePregunta() {
    const examen = this.examen();
    if (!examen) return;
    
    if (this.currentQuestionIndex() < examen.preguntas.length - 1) {
      this.currentQuestionIndex.update(i => i + 1);
    }
  }

  /**
   * Navega a la pregunta anterior
   */
  anteriorPregunta() {
    if (this.currentQuestionIndex() > 0) {
      this.currentQuestionIndex.update(i => i - 1);
    }
  }

  /**
   * Navega a una pregunta específica
   */
  irAPregunta(index: number) {
    const examen = this.examen();
    if (!examen) return;
    
    if (index >= 0 && index < examen.preguntas.length) {
      this.currentQuestionIndex.set(index);
    }
  }

  /**
   * Muestra el diálogo de confirmación para finalizar
   */
  confirmarFinalizarExamen() {
    const examen = this.examen();
    if (!examen) return;
    
    const respondidas = this.respuestas.size;
    const totales = examen.preguntas.length;
    
    if (respondidas < totales) {
      const pendientes = totales - respondidas;
      this.notificationService.warning(
        'Preguntas sin responder',
        `Tienes ${pendientes} pregunta(s) sin responder. ¿Deseas continuar?`
      );
    }
    
    this.showConfirmDialog.set(true);
  }

  /**
   * Finaliza el intento de examen
   */
  finalizarExamen() {
    if (!this.intento()) return;
    
    this.showConfirmDialog.set(false);
    this.isLoading.set(true);
    this.detenerTemporizador();
    
    this.examenesService.finalizarIntento(this.intento()!.id_intento).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok) {
          this.examenFinalizado.set(true);
          
          // Si muestra resultados inmediatos, cargarlos
          const examen = this.examen();
          if (examen?.mostrar_resultados_inmediatos) {
            this.cargarResultados();
          } else {
            this.notificationService.success(
              '¡Examen finalizado!',
              'Tu examen ha sido enviado para revisión'
            );
            setTimeout(() => this.goBack(), 2000);
          }
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo finalizar el examen'
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al finalizar examen:', error);
        this.notificationService.error(
          'Error',
          'No se pudo finalizar el examen'
        );
      }
    });
  }

  /**
   * Carga los resultados del examen
   */
  cargarResultados() {
    if (!this.intento()) return;
    
    this.examenesService.getResultados(this.intento()!.id_intento).subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          this.resultados.set(response.data);
          this.mostrandoResultados.set(true);
        }
      },
      error: (error) => {
        console.error('Error al cargar resultados:', error);
      }
    });
  }

  /**
   * Inicia el temporizador del examen
   */
  private iniciarTemporizador() {
    this.timerSubscription = interval(1000).subscribe(() => {
      this.tiempoRestante.update(t => t - 1);
      this.tiempoTranscurrido.update(t => t + 1);
      
      // Advertencia cuando quedan 5 minutos
      if (this.tiempoRestante() === 300 && !this.showTimeWarning()) {
        this.showTimeWarning.set(true);
        this.notificationService.warning(
          '⏰ Tiempo',
          'Te quedan 5 minutos para finalizar el examen'
        );
      }
      
      // Tiempo agotado
      if (this.tiempoRestante() <= 0) {
        this.detenerTemporizador();
        this.notificationService.error(
          'Tiempo agotado',
          'El tiempo del examen ha finalizado'
        );
        this.finalizarExamen();
      }
    });
  }

  /**
   * Detiene el temporizador
   */
  private detenerTemporizador() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  /**
   * Limpia preguntas duplicadas de un array y retorna el array limpio
   * Versión pura que no modifica el estado
   */
  private limpiarPreguntasDuplicadas(preguntas: Pregunta[]): Pregunta[] {
    if (!preguntas || preguntas.length === 0) return [];
    
    const idsVistos = new Set<number>();
    const preguntasUnicas: Pregunta[] = [];
    const duplicadasInfo: { id: number; texto: string; indice: number }[] = [];
    
    preguntas.forEach((pregunta, indice) => {
      if (!idsVistos.has(pregunta.id_pregunta)) {
        idsVistos.add(pregunta.id_pregunta);
        preguntasUnicas.push(pregunta);
      } else {
        duplicadasInfo.push({
          id: pregunta.id_pregunta,
          texto: pregunta.texto_pregunta.substring(0, 50) + '...',
          indice: indice
        });
        console.warn(`⚠️ Pregunta duplicada eliminada: ID ${pregunta.id_pregunta} en índice ${indice}`);
      }
    });
    
    if (duplicadasInfo.length > 0) {
      console.error(`🚨 [DUPLICADOS] Se encontraron ${duplicadasInfo.length} pregunta(s) duplicada(s)`);
      console.error('📋 [DUPLICADOS] Detalles completos:', duplicadasInfo);
      console.error(`📊 [DUPLICADOS] Originales: ${preguntas.length}, Únicas: ${preguntasUnicas.length}`);
      console.error(`🌍 [DUPLICADOS] Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
      console.error(`🕐 [DUPLICADOS] Timestamp: ${new Date().toISOString()}`);
      
      // Mostrar notificación al usuario
      this.notificationService.info(
        'Sistema de validación',
        `Se detectaron y corrigieron ${duplicadasInfo.length} pregunta(s) duplicada(s). Examen validado correctamente.`
      );
    }
    
    return preguntasUnicas;
  }

  /**
   * Elimina preguntas duplicadas basándose en el id_pregunta (método legacy)
   * Mantenido para compatibilidad
   */
  private eliminarPreguntasDuplicadas() {
    const examen = this.examen();
    if (!examen || !examen.preguntas) return;
    
    const preguntasLimpias = this.limpiarPreguntasDuplicadas(examen.preguntas);
    
    if (preguntasLimpias.length !== examen.preguntas.length) {
      this.examen.set({ ...examen, preguntas: preguntasLimpias });
      console.log(`🔍 Examen ID: ${examen.id_examen}, Intento ID: ${this.intento()?.id_intento || 'N/A'}`);
      console.log(`🔀 Aleatorizar preguntas: ${examen.aleatorizar_preguntas}, Aleatorizar opciones: ${examen.aleatorizar_opciones}`);
    }
  }

  /**
   * Reporta duplicados al backend para investigación
   */
  private reportarDuplicadosAlBackend(idExamen: number, total: number, unicas: number) {
    // Log detallado para el equipo de desarrollo
    const reportData = {
      id_examen: idExamen,
      preguntas_totales: total,
      preguntas_unicas: unicas,
      duplicados_detectados: total - unicas,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      idioma: navigator.language
    };
    
    console.error('🚨 [REPORTE DUPLICADOS]', JSON.stringify(reportData, null, 2));
    
    // TODO: Aquí puedes enviar este reporte a un endpoint de logging si lo deseas
    // this.http.post(`${API_URL}/logs/duplicados`, reportData).subscribe();
  }

  /**
   * Aleatoriza el orden de las preguntas usando Fisher-Yates shuffle
   */
  private aleatorizarPreguntas() {
    const examen = this.examen();
    if (!examen) return;
    
    const preguntas = [...examen.preguntas];
    
    // Fisher-Yates shuffle algorithm
    for (let i = preguntas.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [preguntas[i], preguntas[j]] = [preguntas[j], preguntas[i]];
    }
    
    this.examen.set({ ...examen, preguntas });
  }

  /**
   * Aleatoriza el orden de las opciones en cada pregunta usando Fisher-Yates shuffle
   * Solo aleatoriza preguntas de tipo opción múltiple o verdadero/falso
   */
  private aleatorizarOpciones() {
    const examen = this.examen();
    if (!examen) return;
    
    const preguntasActualizadas = examen.preguntas.map(pregunta => {
      // Solo aleatorizar si tiene opciones y es tipo opción múltiple o verdadero/falso
      if (!pregunta.opciones || pregunta.opciones.length === 0) {
        return pregunta;
      }
      
      // No aleatorizar preguntas de respuesta corta o larga
      if (pregunta.tipo_pregunta === 'respuesta_corta' || pregunta.tipo_pregunta === 'respuesta_larga') {
        return pregunta;
      }
      
      // Crear copia de las opciones para aleatorizar
      const opciones = [...pregunta.opciones];
      
      // Fisher-Yates shuffle algorithm
      for (let i = opciones.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [opciones[i], opciones[j]] = [opciones[j], opciones[i]];
      }
      
      return { ...pregunta, opciones };
    });
    
    this.examen.set({ ...examen, preguntas: preguntasActualizadas });
  }

  /**
   * Formatea el tiempo en formato mm:ss
   */
  formatTime(segundos: number): string {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Verifica si una pregunta ha sido respondida
   */
  isPreguntaRespondida(idPregunta: number): boolean {
    return this.respuestas.has(idPregunta);
  }

  /**
   * Obtiene la respuesta guardada para una pregunta
   */
  getRespuesta(idPregunta: number): RespuestaIntento | undefined {
    return this.respuestas.get(idPregunta);
  }

  /**
   * Vuelve al curso
   */
  goBack() {
    if (this.courseId) {
      this.router.navigate(['/course', this.courseId, 'watch']);
    } else {
      this.router.navigate(['/dashboard/home']);
    }
  }

  /**
   * Cancela el diálogo de confirmación
   */
  cancelarFinalizar() {
    this.showConfirmDialog.set(false);
  }

  /**
   * Formatea la fecha del intento
   */
  formatAttemptDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-SV', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Formatea el tiempo transcurrido del intento
   */
  formatAttemptTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs} segundos`;
    }
    return `${mins} min ${secs} seg`;
  }

  /**
   * Obtiene el mejor intento (mayor porcentaje)
   */
  getBestAttempt(): any | null {
    const intentos = this.todosLosIntentos();
    if (intentos.length === 0) return null;
    
    return intentos.reduce((best, current) => {
      const bestScore = parseFloat(best.porcentaje || '0');
      const currentScore = parseFloat(current.porcentaje || '0');
      return currentScore > bestScore ? current : best;
    });
  }
}

